/**
 * useURLSync Hook
 * URL parameter synchronization
 */

import { useEffect, useCallback } from 'react';
import { useRouter, usePathname, ReadonlyURLSearchParams } from 'next/navigation';
import { BookingData } from '../utils/bookingConstants.js';

export function useURLSync(
  searchParams: ReadonlyURLSearchParams | null,
  bookingData: BookingData,
  eventType: string | null
) {
  const router = useRouter();
  const pathname = usePathname();

  const updateURL = useCallback(
    (data: Partial<BookingData>) => {
      // In event type mode, preserve eventType and other params, only update slot
      if (eventType) {
        const params = new URLSearchParams(searchParams?.toString() || '');
        // Preserve eventType, name, email params
        if (!params.has('eventType') && eventType) {
          params.set('eventType', eventType);
        }
        if (data.selectedSlot) {
          params.set('slot', data.selectedSlot.start);
        }
        const newURL = params.toString() ? `${pathname || ''}?${params.toString()}` : pathname || '';
        router.replace(newURL, { scroll: false });
        return;
      }

      // Non-event-type mode: update URL normally
      const params = new URLSearchParams();

      // Preserve an explicit ?tz= so a rep can send a link pinned to the
      // prospect's zone and it survives every slot/step URL rewrite.
      const tzParam = searchParams?.get('tz');
      if (tzParam) {
        params.set('tz', tzParam);
      }

      if (data.meetingDuration && data.meetingDuration !== 30) {
        params.set('duration', data.meetingDuration.toString());
      }
      if (data.bookeeEmail) {
        params.set('email', data.bookeeEmail);
      }
      if (data.selectedSlot) {
        params.set('slot', data.selectedSlot.start);
      }

      const newURL = params.toString() ? `${pathname || ''}?${params.toString()}` : pathname || '';
      router.replace(newURL, { scroll: false });
    },
    [eventType, searchParams, pathname, router]
  );

  return { updateURL };
}
